import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Mail, MessageSquare, Settings, CheckCircle, XCircle, Eye, EyeOff, RefreshCw, CreditCard, Shield, AlertTriangle, Cpu } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Settings Integrations Tab Component
export function SettingsIntegrations() {
  const [integrations, setIntegrations] = useState([])
  const [stripeConfig, setStripeConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingIntegration, setEditingIntegration] = useState(null)
  const [showApiKey, setShowApiKey] = useState({})

  // Load integrations and Stripe config
  useEffect(() => {
    loadIntegrations()
    loadStripeConfig()
  }, [])

  const loadIntegrations = async () => {
    try {
      setLoading(true)
      setError('')
      
      const { data } = await axios.get(`${API}/admin/settings/integrations`, { withCredentials: true })
      setIntegrations(data)
      
    } catch (err) {
      setError('Failed to load integrations')
      console.error('Integrations error:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadStripeConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stripe/status`, { withCredentials: true })
      setStripeConfig(data)
    } catch (err) {
      // Only log error if it's not a 403 (permission denied)
      if (err.response?.status !== 403) {
        console.error('Stripe config error:', err)
      }
    }
  }

  const updateIntegration = async (service, updates) => {
    try {
      await axios.patch(`${API}/admin/settings/integrations/${service}`, updates, { withCredentials: true })
      await loadIntegrations()
      setEditingIntegration(null)
    } catch (err) {
      alert(`Failed to update integration: ${err.response?.data?.detail || err.message}`)
    }
  }

  const getServiceIcon = (service) => {
    switch (service) {
      case 'resend': return Mail
      case 'sendgrid': return Mail
      case 'slack': return MessageSquare
      case 'stripe': return CreditCard
      case 'openai': return Cpu
      default: return Settings
    }
  }

  const getServiceColor = (service) => {
    switch (service) {
      case 'resend': return 'text-blue-600'
      case 'sendgrid': return 'text-green-600'
      case 'slack': return 'text-purple-600'
      case 'stripe': return 'text-indigo-600'
      case 'openai': return 'text-emerald-600'
      default: return 'text-gray-600'
    }
  }

  const toggleApiKeyVisibility = (service) => {
    setShowApiKey(prev => ({
      ...prev,
      [service]: !prev[service]
    }))
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-pulse">Loading integrations...</div>
      </div>
    )
  }

  return (
    <div className="settings-integrations">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-primary mb-2">Integration Settings</h2>
          <p className="text-muted">Manage API keys and third-party service integrations</p>
        </div>
        <button 
          onClick={() => {
            loadIntegrations()
            loadStripeConfig()
          }} 
          className="btn secondary"
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error mb-6" role="alert">
          {error}
        </div>
      )}

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Stripe Integration Card */}
        <StripeIntegrationCard 
          config={stripeConfig}
          onUpdate={loadStripeConfig}
        />
        
        {/* Regular Integrations */}
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.service}
            integration={integration}
            onUpdate={updateIntegration}
            getServiceIcon={getServiceIcon}
            getServiceColor={getServiceColor}
            showApiKey={showApiKey[integration.service]}
            onToggleApiKey={() => toggleApiKeyVisibility(integration.service)}
            isEditing={editingIntegration === integration.service}
            onStartEditing={() => setEditingIntegration(integration.service)}
            onStopEditing={() => setEditingIntegration(null)}
          />
        ))}
      </div>

      {/* Integration Guide */}
      <div className="mt-8 p-6 bg-blue-50 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">Integration Setup Guide</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p><strong>Stripe:</strong> Get your API keys from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard → Developers → API keys</a></p>
          <p><strong>OpenAI:</strong> Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">OpenAI Platform → API Keys</a></p>
          <p><strong>Resend:</strong> Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">resend.com/api-keys</a></p>
          <p><strong>SendGrid:</strong> Create an API key at <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="underline">SendGrid Settings</a></p>
          <p><strong>Slack:</strong> Set up a bot token at <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="underline">api.slack.com/apps</a></p>
        </div>
      </div>
    </div>
  )
}

// Individual Integration Card Component
function IntegrationCard({ 
  integration, 
  onUpdate, 
  getServiceIcon, 
  getServiceColor, 
  showApiKey, 
  onToggleApiKey, 
  isEditing, 
  onStartEditing, 
  onStopEditing 
}) {
  const [formData, setFormData] = useState({
    api_key: '',
    is_active: integration.is_active
  })
  const [updating, setUpdating] = useState(false)

  const Icon = getServiceIcon(integration.service)
  const colorClass = getServiceColor(integration.service)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setUpdating(true)
    
    const updates = {}
    if (formData.api_key.trim()) {
      updates.api_key = formData.api_key.trim()
    }
    if (formData.is_active !== integration.is_active) {
      updates.is_active = formData.is_active
    }

    if (Object.keys(updates).length > 0) {
      await onUpdate(integration.service, updates)
    }
    
    setUpdating(false)
    onStopEditing()
  }

  const handleCancel = () => {
    setFormData({
      api_key: '',
      is_active: integration.is_active
    })
    onStopEditing()
  }

  const isConfigured = integration.api_key && !integration.api_key.includes('placeholder')

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center ${colorClass}`}>
            <Icon size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-primary">{integration.name}</h3>
            <p className="text-sm text-muted capitalize">{integration.service}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isConfigured ? (
            <CheckCircle className="text-green-500" size={16} />
          ) : (
            <XCircle className="text-red-500" size={16} />
          )}
          <span className={`text-xs px-2 py-1 rounded-full ${
            integration.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {integration.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              API Key
            </label>
            <input
              type="password"
              className="input w-full text-sm"
              value={formData.api_key}
              onChange={(e) => setFormData({...formData, api_key: e.target.value})}
              placeholder={isConfigured ? "Enter new API key to update" : "Enter API key"}
            />
          </div>
          
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
              <span className="text-sm text-primary">Enable this integration</span>
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              className="btn primary text-xs flex-1"
              disabled={updating}
            >
              {updating ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="btn secondary text-xs"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-primary">API Key</span>
              {isConfigured && (
                <button
                  onClick={onToggleApiKey}
                  className="text-xs text-muted hover:text-primary"
                >
                  {showApiKey ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              )}
            </div>
            <div className="text-sm text-muted font-mono bg-gray-50 p-2 rounded">
              {isConfigured ? (
                showApiKey ? integration.api_key : '••••••••••••••••'
              ) : (
                'Not configured'
              )}
            </div>
          </div>

          <div className="text-xs text-muted">
            <div>Status: {integration.test_status}</div>
            {integration.last_tested && (
              <div>Last tested: {new Date(integration.last_tested).toLocaleDateString()}</div>
            )}
            <div>Created: {new Date(integration.created_at).toLocaleDateString()}</div>
          </div>

          <button
            onClick={onStartEditing}
            className="btn secondary text-xs w-full"
          >
            Configure
          </button>
        </div>
      )}
    </div>
  )
}

// Stripe Integration Card Component 
function StripeIntegrationCard({ config, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [healthChecking, setHealthChecking] = useState(false)
  const [formData, setFormData] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    environment: 'test'
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/stripe/configure`, formData, { withCredentials: true })
      await onUpdate()
      setIsEditing(false)
      setFormData({
        publishable_key: '',
        secret_key: '',
        webhook_secret: '',
        environment: 'test'
      })
    } catch (err) {
      alert(`Failed to configure Stripe: ${err.response?.data?.detail || err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleHealthCheck = async () => {
    setHealthChecking(true)
    try {
      await axios.post(`${BACKEND_URL}/api/admin/stripe/health-check`, {}, { withCredentials: true })
      await onUpdate()
    } catch (err) {
      alert(`Health check failed: ${err.response?.data?.detail || err.message}`)
    } finally {
      setHealthChecking(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      publishable_key: '',
      secret_key: '',
      webhook_secret: '',
      environment: 'test'
    })
    setIsEditing(false)
  }

  const getHealthIcon = () => {
    if (!config?.configured) return <XCircle className="text-red-500" size={16} />
    
    switch (config?.health_status) {
      case 'healthy': return <CheckCircle className="text-green-500" size={16} />
      case 'failed': return <XCircle className="text-red-500" size={16} />
      case 'untested': return <AlertTriangle className="text-yellow-500" size={16} />
      default: return <XCircle className="text-red-500" size={16} />
    }
  }

  const getHealthText = () => {
    if (!config?.configured) return 'Not configured'
    
    switch (config?.health_status) {
      case 'healthy': return 'Healthy'
      case 'failed': return 'Failed'
      case 'untested': return 'Untested'
      default: return 'Unknown'
    }
  }

  return (
    <div className="card p-6 border-2 border-indigo-200 bg-indigo-50/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
            <CreditCard size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-primary flex items-center gap-2">
              Stripe Integration
              <Shield className="text-indigo-600" size={14} />
            </h3>
            <p className="text-sm text-muted">Payment processing & billing</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {getHealthIcon()}
          <span className={`text-xs px-2 py-1 rounded-full ${
            config?.configured ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {config?.configured ? config.environment.toUpperCase() : 'Not configured'}
          </span>
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Environment
            </label>
            <select
              className="input w-full text-sm"
              value={formData.environment}
              onChange={(e) => setFormData({...formData, environment: e.target.value})}
            >
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Publishable Key
            </label>
            <input
              type="text"
              className="input w-full text-sm font-mono"
              value={formData.publishable_key}
              onChange={(e) => setFormData({...formData, publishable_key: e.target.value})}
              placeholder="pk_test_..."
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Secret Key
            </label>
            <input
              type="password"
              className="input w-full text-sm font-mono"
              value={formData.secret_key}
              onChange={(e) => setFormData({...formData, secret_key: e.target.value})}
              placeholder="sk_test_..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Webhook Signing Secret
            </label>
            <input
              type="password"
              className="input w-full text-sm font-mono"
              value={formData.webhook_secret}
              onChange={(e) => setFormData({...formData, webhook_secret: e.target.value})}
              placeholder="whsec_..."
              required
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Shield className="text-yellow-600 mt-0.5" size={16} />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Security Note</p>
                <p>Keys are encrypted before storage. Only super admins can configure Stripe.</p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              className="btn primary text-xs flex-1"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="btn secondary text-xs"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          {config?.configured ? (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-primary">Publishable Key</span>
                  <div className="text-muted font-mono bg-gray-50 p-2 rounded mt-1">
                    {config.publishable_key_masked}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-primary">Secret Key</span>
                  <div className="text-muted font-mono bg-gray-50 p-2 rounded mt-1">
                    {config.secret_key_masked}
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-primary">Webhook Secret</span>
                  <div className="text-muted font-mono bg-gray-50 p-2 rounded mt-1">
                    {config.webhook_secret_masked}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <div className="text-xs text-muted">
                  Status: {getHealthText()}
                  {config.last_health_check && (
                    <div>Last check: {new Date(config.last_health_check).toLocaleString()}</div>
                  )}
                </div>
                <button
                  onClick={handleHealthCheck}
                  className="btn secondary text-xs"
                  disabled={healthChecking}
                >
                  {healthChecking ? 'Checking...' : 'Health Check'}
                </button>
              </div>

              <button
                onClick={() => setIsEditing(true)}
                className="btn primary text-xs w-full"
              >
                Update Configuration
              </button>
            </>
          ) : (
            <>
              <div className="text-center py-4">
                <CreditCard className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-sm text-muted mb-2">Stripe not configured</p>
                <p className="text-xs text-muted">Payment processing unavailable</p>
              </div>
              
              <button
                onClick={() => setIsEditing(true)}
                className="btn primary text-xs w-full"
              >
                Configure Stripe
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default SettingsIntegrations